import { useCallback } from 'react';

import { marksReportService } from '@/api/domains/attendance/services/marksReport.service';
import { useHybridExport } from '@/hooks/ui/useHybridExport';
import { useTranslation } from '@/i18n';
import type { EmployeeFilteredStatus } from '@/api/domains/employee/types';
import type { UIExportFormat } from '@/types/fileExport.types';


// Sentinela "Todos" de los filtros rápidos: se traduce a undefined (sin filtro) en el payload.
const ALL_FILTER_VALUE = 'todos';

function joinOrUndefined(ids: string[]): string | undefined {
  return ids.length > 0 ? ids.join(',') : undefined;
}

export interface UseMarksReportExportParams {
  dateFrom: string;
  dateTo: string;
  eventId: string;
  locationId: string;
  controlId: string;
  employeeIds: number[];
  companyIds: string[];
  areaIds: string[];
  positionIds: string[];
  costCenterIds: string[];
  contractModalityIds: string[];
  groupIds: string[];
  employeeStatus: EmployeeFilteredStatus;
  searchText: string | undefined;
}

export interface UseMarksReportExportResult {
  isExporting: boolean;
  onExport: (format: UIExportFormat) => Promise<void>;
}

/**
 * Descarga del informe de marcajes a Excel (ASIS-5314). Estrategia híbrida sync/async:
 * el backend responde 200 con el archivo cuando el volumen está bajo el umbral síncrono, o 202
 * (job en background) cuando lo supera. Envía los mismos filtros que el listado — incluido el
 * texto de búsqueda activo — sin paginación: exporta todo el universo que cumple los filtros.
 *
 * Delega el branching sync/202, el estado `isExporting` y las notificaciones en `useHybridExport`
 * (análogo a `useExport` pero con soporte para la rama asíncrona 202); aquí solo se arma el payload
 * y se traducen los textos.
 */
export function useMarksReportExport(
  params: UseMarksReportExportParams,
): UseMarksReportExportResult {
  const { t: translate } = useTranslation();

  const requestExport = useCallback(
    () => marksReportService.export({
      dateFrom: params.dateFrom,
      dateTo: params.dateTo,
      eventIds: params.eventId === ALL_FILTER_VALUE ? undefined : params.eventId,
      markLocationIds: params.locationId === ALL_FILTER_VALUE ? undefined : params.locationId,
      controlIds: params.controlId === ALL_FILTER_VALUE ? undefined : params.controlId,
      employeeIds: joinOrUndefined(params.employeeIds.map(String)),
      companyIds: joinOrUndefined(params.companyIds),
      areaIds: joinOrUndefined(params.areaIds),
      positionIds: joinOrUndefined(params.positionIds),
      costCenterIds: joinOrUndefined(params.costCenterIds),
      contractModalityIds: joinOrUndefined(params.contractModalityIds),
      groupIds: joinOrUndefined(params.groupIds),
      employeeStatus: params.employeeStatus,
      searchText: params.searchText || undefined,
    }),
    [params],
  );

  const { isExporting, runExport } = useHybridExport({
    requestExport,
    messages: {
      error: translate('attendance:marksReport.messages.exportError'),
      success: translate('attendance:marksReport.messages.exportSuccess'),
      queued: translate('attendance:marksReport.messages.exportQueued'),
    },
  });

  return { isExporting, onExport: runExport };
}

export default useMarksReportExport;
